import React from 'react';
import type { RawData } from '../../../types';
import { Box, Grid, IconButton, FormLabel, styled, Typography } from '@beeline.com/bp-design-system';
import { Column, ColumnType } from '../types';

type UserTableColumnProps<T extends string> = {
  column: Column<T>;
  entries: RawData;
  onIgnore: (index: number) => void;
  onRevertIgnore: (index: number) => void;
};

function dataAttr(condition: boolean) {
  return condition ? '' : undefined;
}

export const UserTableColumn = <T extends string>(props: UserTableColumnProps<T>) => {
  const {
    column: { header, index, type },
    entries,
    onIgnore,
    onRevertIgnore
  } = props;

  const isIgnored = type === ColumnType.ignored;
  return (
    <Box>
      <StyledBox padding="12px" $ignored={isIgnored}>
        <Grid container alignItems="center" minHeight={10} width="100%" position="relative">
          <StyledLabel htmlFor={`select-field-${header}`} data-ignored={dataAttr(isIgnored)}>
            {header}
          </StyledLabel>
          <StyledIconContainer>
            {type === ColumnType.ignored ? (
              <IconButton
                iconName="undo"
                slotProps={{ tooltip: { title: 'Undo' } }}
                dataTest="undo-ignore-column"
                onClick={() => onRevertIgnore(index)}
              />
            ) : (
              <IconButton
                iconName="close"
                slotProps={{ tooltip: { title: 'Ignore Column' } }}
                dataTest="ignore-column"
                onClick={() => onIgnore(index)}
              />
            )}
          </StyledIconContainer>
        </Grid>
      </StyledBox>
      {entries.map((entry, index) => (
        <StyledBox padding="12px" key={(entry || '') + index} $ignored={isIgnored}>
          <Typography data-ignored={dataAttr(isIgnored)}>{entry}</Typography>
        </StyledBox>
      ))}
    </Box>
  );
};

const StyledBox = styled(Box)<{ $ignored?: boolean }>`
    border-bottom: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
    text-decoration: ${({ $ignored }) => ($ignored ? 'line-through' : undefined)};
`;

const StyledIconContainer = styled('div')`
    margin-left: 4px;
    margin-top: -25px;
    height: 14px;
`;

const StyledLabel = styled(FormLabel)`
  max-width: calc(100% - 45px); // Allow space for X button
`;
